import * as React from "react";

import type { QueryData } from "@alaffia-technology-solutions/client-sdk-react-query";
import type {
  Agent,
  AgentMessage,
  Case,
  Queue,
  Thread,
} from "@alaffia-technology-solutions/schema";
import type { Awaitable } from "@alaffia-technology-solutions/types";

import {
  AgentMessageUserValidationStatus,
  MessagesOrderField,
  PromptFeedbackLevel,
  SortDirection,
} from "@alaffia-technology-solutions/client-sdk";
import {
  apiClient,
  createUseInfiniteQueryPagination,
} from "@alaffia-technology-solutions/client-sdk-react-query";

import { useStickToBottom } from "use-stick-to-bottom";

import { useCreateMessageThread } from "../queues/hooks";
import { parseTaskArtifactOutput } from "./utils";

interface UseChatboxStateOptions {
  caseId: Case["id"];
}

export function useChatboxState({ caseId }: UseChatboxStateOptions) {
  const threads = apiClient.cases.getThreads.useQuery({
    input: {
      caseId,
    },
  });

  const currentThread = threads.data?.[0];

  const createThread = useCreateMessageThread();

  return {
    createThread,
    currentThread,
    threads,
  };
}

export interface UseThreadStateOptions {
  threadId: Thread["id"] | undefined;
  queueId?: Queue["id"] | null;
}

export const threadMessagesPagination = createUseInfiniteQueryPagination<
  QueryData<typeof apiClient.cases.getThread>
>({
  pageSize: 5,
  orderBy: [
    { field: MessagesOrderField.CREATED_AT, direction: SortDirection.DESC },
  ],
  getConnection: (page) => page?.messagesConnection,
  initialPageParam: {
    cursor: "",
    direction: "forward",
  },
});

export function useThreadState({ threadId, queueId }: UseThreadStateOptions) {
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  // We use useIsMutating here instead of `createMessageMutation.isLoading`
  // because useIsMutating reads the mutation status from the global mutation cache,
  // so it will preserve the status when the Chatbox is closed and reopened.
  const isPromptMutating = apiClient.cases.prompt.useIsMutating() > 0;

  const thread = apiClient.cases.getThread.useInfiniteQuery(
    { id: threadId! },
    {
      // This prevents the threads refetching (on window re-focusing
      // or upon closing and reopening the Chatbox window)
      // and overwriting the optimistic updates made in the `prompt` mutation
      // before the prompt mutation is completed.
      enabled: Boolean(threadId) && !isPromptMutating,
      staleTime: Infinity,
      getNextPageParam: threadMessagesPagination.getNextPageParam,
      getPreviousPageParam: threadMessagesPagination.getPreviousPageParam,
    },
    {
      initialPageParam: threadMessagesPagination.initialPageParam,
      internalInputFn: threadMessagesPagination.internalInputFn,
    },
  );

  const messages = React.useMemo(
    () =>
      thread.data?.pages.flatMap(
        (page) => page?.messagesConnection.nodes ?? [],
      ) ?? [],
    [thread.data?.pages],
  );

  const { scrollRef, contentRef, scrollToBottom } = useStickToBottom({
    initial: "instant",
  });

  const fetchNextPage = React.useCallback(
    () => thread.fetchNextPage(),
    [thread.fetchNextPage],
  );

  const createMessageMutation = useCreateMessage({
    queueId,
    onMutate: scrollToBottom,
  });

  const createMessage = React.useCallback(
    (
      input: Omit<
        Parameters<typeof createMessageMutation.mutate>[0]["input"],
        "threadId"
      >,
    ) => {
      if (!threadId) {
        // This should never happen because every case should have at least one thread
        throw new Error("No thread found");
      }

      return createMessageMutation.mutate({
        input: {
          threadId,
          ...input,
        },
      });
    },
    [createMessageMutation, threadId],
  );

  return {
    thread,
    messages,
    createMessage,
    isPromptMutating,
    wrapperRef,
    scrollableRef: scrollRef,
    contentRef: contentRef,
    fetchNextPage,
  };
}

interface UseCreateMessageOptions {
  queueId?: Queue["id"] | null;
  onMutate?: () => Awaitable<unknown>;
}

type Messages = NonNullable<
  ReturnType<typeof apiClient.cases.getThread.useQuery>["data"]
>["messagesConnection"]["nodes"];

export function useCreateMessage({
  queueId,
  onMutate,
}: UseCreateMessageOptions) {
  const getAllCasesCtx = apiClient.cases.getAllCases.useQueryContext();
  const getThreadCtx = apiClient.cases.getThread.useQueryContext();

  const updateFirstPageMessages = (
    threadId: Thread["id"],
    updater: (messages: Messages) => Messages,
  ) => {
    const previousThread = getThreadCtx.getInfiniteData(
      {
        id: threadId,
      },
      threadMessagesPagination.internalInputFn,
    );
    const firstPage = previousThread?.pages[0];
    if (previousThread && firstPage) {
      const newFirstPage = {
        ...firstPage,
        messagesConnection: {
          ...firstPage.messagesConnection,
          nodes: updater(firstPage.messagesConnection.nodes),
        },
      };
      const newThread = {
        ...previousThread,
        pages: [newFirstPage, ...previousThread.pages.slice(1)],
      };

      getThreadCtx.setInfiniteData(
        { id: threadId },
        newThread,
        threadMessagesPagination.internalInputFn,
      );

      return true;
    }

    return false;
  };

  return apiClient.cases.prompt.useMutation({
    onSuccess: async (result, { input: { threadId } }) => {
      if (!result) return;

      await getAllCasesCtx.invalidate({ input: { queueId } });

      await getThreadCtx.cancel({ id: threadId });

      updateFirstPageMessages(threadId, (messages) => [
        result,
        ...messages.slice(1),
      ]);
    },
    onMutate: async ({ input: { promptText, inputState, threadId } }) => {
      await getThreadCtx.cancel({
        id: threadId,
      });

      const previousThread = getThreadCtx.getInfiniteData(
        {
          id: threadId,
        },
        threadMessagesPagination.internalInputFn,
      );

      updateFirstPageMessages(threadId, (messages) => {
        const utcDate = new Date().toISOString();
        const optimisticMessage = {
          __typename: "Message" as const,
          id: `${utcDate}-${messages.length + 1}`,
          promptText,
          promptInputState: inputState,
          threadId,
          createdAt: utcDate,
          response: null,
        };
        return [optimisticMessage, ...messages];
      });

      void onMutate?.();

      return { previousThread };
    },
    onError: async (_error, { input: { threadId } }, mutationContext) => {
      await getThreadCtx.cancel({
        id: threadId,
      });

      const previousThread = mutationContext?.previousThread;

      const didUpdate = updateFirstPageMessages(threadId, (messages) =>
        messages[0]
          ? [
              {
                ...messages[0],
                __typename: "Message",
                feedback: [
                  {
                    __typename: "PromptFeedback",
                    message:
                      "Something went wrong, please try your request again",
                    level: PromptFeedbackLevel.ERROR,
                  },
                ],
              },
              ...messages.slice(1),
            ]
          : messages,
      );

      if (!didUpdate && previousThread) {
        getThreadCtx.setInfiniteData(
          {
            id: threadId,
          },
          previousThread,
          threadMessagesPagination.internalInputFn,
        );
      }
    },
  });
}

interface UseAcceptAgentMessageOptions {
  agentId: Agent["id"];
  onSuccess?: () => void;
}

export function useAcceptAgentMessage({
  agentId,
  onSuccess: externalOnSuccess,
}: UseAcceptAgentMessageOptions) {
  const { onMutate, onError, onSuccess } =
    useOptimisticUpdateAgentMessageUserValidationStatus({
      agentId,
      userValidationStatus: AgentMessageUserValidationStatus.UNSET,
      onSuccess: externalOnSuccess,
    });

  return apiClient.cases.acceptAgentMessage.useMutation({
    onMutate,
    onError,
    onSuccess,
  });
}

interface UseUnsetAgentMessageUserValidationOptions {
  agentId: Agent["id"];
  onSuccess?: () => void;
}

export function useUnsetAgentMessageUserValidation({
  agentId,
  onSuccess: externalOnSuccess,
}: UseUnsetAgentMessageUserValidationOptions) {
  const { onMutate, onError, onSuccess } =
    useOptimisticUpdateAgentMessageUserValidationStatus({
      agentId,
      userValidationStatus: AgentMessageUserValidationStatus.UNSET,
      onSuccess: externalOnSuccess,
    });

  return apiClient.cases.unsetAgentMessageUserValidation.useMutation({
    onMutate,
    onError,
    onSuccess,
  });
}

interface UseOptimisticUpdateAgentMessageUserValidationStatusOptions {
  agentId: Agent["id"];
  userValidationStatus: AgentMessageUserValidationStatus;
  onSuccess?: () => void;
}

function useOptimisticUpdateAgentMessageUserValidationStatus({
  agentId,
  userValidationStatus,
  onSuccess: externalOnSuccess,
}: UseOptimisticUpdateAgentMessageUserValidationStatusOptions) {
  const { data: user } = apiClient.auth.getUser.useQuery({});
  const getAgentCtx = apiClient.cases.getAgent.useQueryContext();

  return {
    onMutate: async ({
      agentMessageId,
    }: {
      agentMessageId: AgentMessage["id"];
    }) => {
      await getAgentCtx.cancel({ id: agentId });

      const previousAgent = getAgentCtx.getData({ id: agentId });

      if (previousAgent) {
        const optimisticAgentMessages = previousAgent.messages?.map(
          (message) => {
            if (message.id !== agentMessageId || !user) {
              return message;
            }

            return {
              ...message,
              userValidation: {
                status: userValidationStatus,
                userId: user.id,
                createdAt: new Date().toISOString(),
                author: {
                  id: user.id,
                  firstName: user.firstName,
                  lastName: user.lastName,
                  title: user.title,
                },
              },
            };
          },
        );

        getAgentCtx.setData(
          { id: agentId },
          {
            ...previousAgent,
            messages: optimisticAgentMessages,
          },
        );
      }

      return { previousAgent };
    },
    onError: (
      _error: unknown,
      _variables: unknown,
      context?: {
        previousAgent?: ReturnType<
          typeof apiClient.cases.getAgent.useQuery
        >["data"];
      },
    ) => {
      if (context?.previousAgent) {
        getAgentCtx.setData({ id: agentId }, context.previousAgent);
      }
    },
    onSuccess: () => {
      externalOnSuccess?.();
      return getAgentCtx.invalidate({ id: agentId });
    },
  };
}

export function useParsedTaskArtifactOutput(
  unparsedContent: unknown,
  unparsedSchema: unknown,
) {
  return React.useMemo(
    () => parseTaskArtifactOutput(unparsedContent, unparsedSchema),
    [unparsedContent, unparsedSchema],
  );
}
